import React from "react";
import { Receipt, ShieldCheck, Eye, Check, Percent } from "lucide-react";
import Seo, { faqJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import { Container, Section, Eyebrow, Badge } from "@/components/ui-kit/Section";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/Reveal";
import IncomeCalculator from "@/components/calculators/IncomeCalculator";
import FaqAccordion from "@/components/blocks/FaqAccordion";
import DualAudience from "@/components/blocks/DualAudience";
import Cta from "@/components/ui-kit/Cta";
import { FAQ_GROUPS } from "@/lib/site";

const FEE_FACTS = [
  {
    icon: Percent,
    title: "0.5% of each routed amount",
    body: "The fee is computed per transaction, on the amount actually routed toward your taxes. Nothing else.",
  },
  {
    icon: Eye,
    title: "Shown on every transaction",
    body: "You see the fee on each transaction record, so there is never a surprise line item at the end of a month.",
  },
  {
    icon: ShieldCheck,
    title: "A hard ceiling in code",
    body: "The 0.5 percent cap is enforced in code. You can never be charged more than 0.5 percent of a routed amount.",
  },
];

const INCLUDED = [
  "Automatic set aside on every detected deposit",
  "Read only bank connection through Plaid",
  "Live dashboard of deposits, routed amounts, and fees",
  "Quarterly and annual reports, exportable",
  "Pause, adjust, or withdraw consent anytime",
  "No monthly fee and no setup fee",
];

const FEES_FAQ = FAQ_GROUPS.find((g) => g.id === "fees").items;

export default function Pricing() {
  return (
    <>
      <Seo
        title="ETAP Pricing | 0.5% flat, hard ceiling in code"
        description="ETAP charges a flat 0.5 percent per routed transaction, enforced as a hard ceiling in code. No monthly fee, no setup fee. Try the live calculator."
        path="/pricing"
        jsonLd={[
          faqJsonLd(FEES_FAQ),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Pricing", path: "/pricing" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-10 sm:pt-40 sm:pb-14">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(50% 45% at 50% 0%, rgba(197,254,237,0.6) 0%, rgba(197,254,237,0) 62%)",
          }}
        />
        <Container>
          <Reveal className="mx-auto max-w-3xl text-center">
            <Badge className="mx-auto">One flat fee, nothing hidden</Badge>
            <h1 className="mt-6 font-display text-[clamp(3.5rem,12vw,8rem)] font-extrabold leading-none tracking-tight text-etap-deep-900">
              0.5%<span className="text-etap-primary">.</span>
            </h1>
            <p className="mt-2 font-display text-2xl font-bold text-etap-deep-700">That is it.</p>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-etap-deep-600">
              ETAP charges 0.5 percent of each routed amount, computed per transaction and shown on every record. It is
              enforced as a hard ceiling in code, so you can never be charged more.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Cta to="/get-started" variant="primary" size="lg" location="pricing-hero" withArrow testId="pricing-hero-cta">
                Start Free Setup
              </Cta>
              <Cta to="#calculator" variant="outline" size="lg" location="pricing-hero-calc" testId="pricing-hero-calc">
                Try the calculator
              </Cta>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Fee facts */}
      <Section className="bg-canvas pt-6">
        <Container>
          <RevealGroup className="grid gap-6 md:grid-cols-3">
            {FEE_FACTS.map((f) => (
              <RevealItem key={f.title} className="rounded-3xl border border-etap-deep-100 bg-white p-8 shadow-etap-sm">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-etap-primary-50 text-etap-primary">
                  <f.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h2 className="mt-6 font-display text-lg font-bold tracking-tight text-etap-deep-800">{f.title}</h2>
                <p className="mt-3 text-etap-deep-600">{f.body}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </Container>
      </Section>

      {/* Agency split visual */}
      <Section className="bg-white pt-0">
        <Container>
          <div className="mx-auto max-w-4xl rounded-3xl border border-etap-mint-300 bg-etap-mint-100 p-8 sm:p-10">
            <Reveal>
              <Eyebrow>Referred by an agency?</Eyebrow>
              <h2 className="mt-3 font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold tracking-tight text-etap-deep-900">
                The same 0.5 percent, split two ways
              </h2>
              <p className="mt-3 max-w-2xl text-etap-deep-700">
                When an agency refers you, the flat fee is divided evenly. You still pay 0.5 percent total, never more.
              </p>
            </Reveal>
            <Reveal delay={0.1} className="mt-7 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl bg-white p-5 text-center shadow-etap-sm">
                <p className="font-display text-3xl font-extrabold text-etap-deep-800">0.25%</p>
                <p className="mt-1 text-sm font-semibold text-etap-deep-600">Referring agency</p>
              </div>
              <div className="rounded-2xl bg-white p-5 text-center shadow-etap-sm">
                <p className="font-display text-3xl font-extrabold text-etap-deep-800">0.25%</p>
                <p className="mt-1 text-sm font-semibold text-etap-deep-600">Platform</p>
              </div>
              <div className="rounded-2xl bg-etap-deep p-5 text-center text-white shadow-etap-sm">
                <p className="font-display text-3xl font-extrabold">0.5%</p>
                <p className="mt-1 text-sm font-semibold text-etap-mint-200">You pay, total</p>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* Calculator */}
      <Section id="calculator" className="bg-canvas pt-0">
        <Container>
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <Reveal>
              <Eyebrow>Run your numbers</Eyebrow>
              <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3rem)] font-bold tracking-tight text-etap-deep-900">
                See the fee on your own income
              </h2>
              <p className="mt-4 text-lg text-etap-deep-600">
                Set a monthly income and a set aside percentage. Watch the routed amount, the fee, and what stays in
                your account update live.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="mx-auto max-w-4xl">
            <IncomeCalculator variant="full" />
          </Reveal>
        </Container>
      </Section>

      {/* Whats included */}
      <Section className="bg-white pt-0">
        <Container>
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <Eyebrow>What is included</Eyebrow>
              <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3rem)] font-bold tracking-tight text-etap-deep-900">
                Everything, for the same flat fee
              </h2>
            </Reveal>
            <RevealGroup className="mt-8 grid gap-3 sm:grid-cols-2">
              {INCLUDED.map((i) => (
                <RevealItem key={i} className="flex items-start gap-3 rounded-2xl border border-etap-deep-100 bg-canvas p-4">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-etap-primary" aria-hidden="true" />
                  <span className="text-sm font-medium text-etap-deep-700">{i}</span>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section className="bg-canvas pt-0">
        <Container>
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <Eyebrow>Pricing questions</Eyebrow>
              <h2 className="mt-4 font-display text-[clamp(1.75rem,3.4vw,2.5rem)] font-bold tracking-tight text-etap-deep-900">
                No asterisks, no surprises
              </h2>
            </Reveal>
            <Reveal className="mt-8">
              <FaqAccordion items={FEES_FAQ} idPrefix="pricing-faq" />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="bg-white pt-0">
        <Container>
          <div className="flex items-center gap-3 mb-8">
            <Receipt className="h-5 w-5 text-etap-primary" aria-hidden="true" />
            <p className="text-sm font-semibold text-etap-deep-600">Pick the path that fits you</p>
          </div>
          <DualAudience location="pricing-dual" />
        </Container>
      </Section>
    </>
  );
}
